import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CURRENT_YEAR } from '../utils/clinicInfo';

const Header = () => {
  const [activeMenu, setActiveMenu] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const location = useLocation();
  
  const menus = [
    { 
      id: '', 
      name: '홈',
      submenu: []
    },
    { 
      id: 'clinic-intro', 
      name: '치과소개',
      submenu: [
        { id: 'clinic-intro?tab=intro', name: '인사말', hash: '#intro' },
        { id: 'clinic-intro?tab=doctors', name: '의료진', hash: '#doctors' },
        { id: 'clinic-intro?tab=story', name: '병원 이야기', hash: '#story' },
        { id: 'clinic-intro?tab=hours', name: '진료시간', hash: '#hours' },
        { id: 'clinic-intro?tab=location', name: '오시는 길', hash: '#location' }
      ]
    },
    { 
      id: 'natural-teeth', 
      name: '자연치아 살리기',
      submenu: [
        { id: 'natural-teeth?tab=resin', name: '레진치료', hash: '#resin' },
        { id: 'natural-teeth?tab=nerve', name: '신경치료', hash: '#nerve' },
        { id: 'natural-teeth?tab=re-nerve', name: '재신경치료', hash: '#re-nerve' },
        { id: 'natural-teeth?tab=prosthetic', name: '보철치료', hash: '#prosthetic' },
        { id: 'natural-teeth?tab=periodontal', name: '치주치료', hash: '#periodontal' }
      ]
    },
    { 
      id: 'front-teeth', 
      name: '앞니 자신감',
      submenu: [
        { id: 'front-teeth?tab=resin-diastema', name: '치간 이개부 레진치료', hash: '#resin-diastema' },
        { id: 'front-teeth?tab=prosthetic-diastema', name: '치간 이개부 보철치료', hash: '#prosthetic-diastema' },
        { id: 'front-teeth?tab=caries-prosthetic', name: '충치 보철치료', hash: '#caries-prosthetic' },
        { id: 'front-teeth?tab=whitening', name: '미백치료', hash: '#whitening' }
      ]
    },
    { 
      id: 'implant', 
      name: '임플란트',
      submenu: [
        { id: 'implant?tab=custom', name: '환자 맞춤형 임플란트', hash: '#custom' },
        { id: 'implant?tab=advanced', name: '고난도 뼈이식 & 상악동 거상 동반 임플란트', hash: '#advanced' },
        { id: 'implant?tab=full-mouth', name: '고난도 전악치료', hash: '#full-mouth' },
        { id: 'implant?tab=revision', name: '임플란트 재수술', hash: '#revision' },
        { id: 'implant?tab=orthodontic', name: '교정 동반 임플란트', hash: '#orthodontic' }
      ]
    },
    { 
      id: 'orthodontics', 
      name: '교정치료',
      submenu: [
        { id: 'orthodontics?tab=non-extraction', name: '비발치 교정', hash: '#non-extraction' },
        { id: 'orthodontics?tab=extraction', name: '발치 교정', hash: '#extraction' },
        { id: 'orthodontics?tab=partial', name: '부분 교정', hash: '#partial' },
        { id: 'orthodontics?tab=orthodontic-implant', name: '교정 동반 임플란트', hash: '#orthodontic-implant' }
      ]
    },
    { 
      id: 'children', 
      name: '어린이 치료',
      submenu: []
    }
  ];
  
  // 데스크톱 메뉴 (홈 메뉴 제외)
  const desktopMenus = menus.slice(1);
  
  // 현재 경로에 따라 활성 메뉴를 설정
  useEffect(() => {
    const path = location.pathname.substring(1); // 앞의 '/' 제거
    if (path === '') {
      setActiveMenu('');
    } else {
      setActiveMenu(path);
    }
  }, [location]);
  
  // 스크롤 이벤트 처리
  useEffect(() => {
    // 이 사이트는 창(window) 대신 body가 스크롤되는 구조라 둘 다 확인
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop;
      if (scrollTop > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    const handleClickOutside = (event) => {
      // 드롭다운 메뉴 외부 클릭 시 닫기
      if (!event.target.closest('.dropdown-container')) {
        setDropdownOpen(null);
      }
    };
    
    // body의 스크롤 이벤트는 위로 전달되지 않아서 capture 단계에서 받음
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  
  // 모바일 메뉴 토글
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // 모바일 메뉴 닫기
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(null); // 모바일 드롭다운도 함께 초기화
  };

  // 드롭다운 핸들러
  const handleMouseEnter = (menuId) => {
    setDropdownOpen(menuId);
  };

  const handleMouseLeave = () => {
    setDropdownOpen(null);
  };

  // 모바일 드롭다운 토글
  const toggleMobileDropdown = (menuId) => {
    setMobileDropdownOpen(mobileDropdownOpen === menuId ? null : menuId);
  };

  // 스크롤 방지 (모바일 메뉴가 열려있을 때)
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      
      // fullPage.js 네비게이션을 숨기기 위한 코드
      const fpNav = document.getElementById('fp-nav');
      if (fpNav) {
        fpNav.style.zIndex = '10'; // z-index를 낮게 설정
      }
    } else {
      document.body.style.overflow = '';
      
      // fullPage.js 네비게이션 복원
      const fpNav = document.getElementById('fp-nav');
      if (fpNav) {
        fpNav.style.zIndex = ''; // 원래 값으로 복원
      }
    }
    
    return () => {
      document.body.style.overflow = '';
      
      // 컴포넌트 언마운트 시 fullPage.js 네비게이션 복원
      const fpNav = document.getElementById('fp-nav');
      if (fpNav) {
        fpNav.style.zIndex = '';
      }
    };
  }, [mobileMenuOpen]);
  
  return (
    <>
      {/* 헤더 */}
      <header className={`fixed top-0 z-[100] w-full transition-all duration-300 ${
        scrolled 
          ? location.pathname === '/' 
            ? 'bg-black/70 backdrop-blur-sm shadow-md'
            : 'bg-black/90 backdrop-blur-md shadow-lg'
          : location.pathname === '/'
            ? 'bg-black/40 backdrop-blur-[1px]'
            : 'bg-black/60 backdrop-blur-sm'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center h-16 md:h-20">
            {/* 로고 및 병원 이름 */}
            <Link to="/" className="flex items-center mr-10" onClick={closeMobileMenu}>
              <img 
                src="/assets/한앤손부부치과_로고_small.webp" 
                alt="한앤손 부부치과 로고" 
                className="h-10 md:h-12 w-auto mr-3"
              />
              <div className="text-xl md:text-2xl font-bold text-white">
                한앤손 부부치과
              </div>
            </Link>
            
            {/* 데스크톱 메뉴 */}
            <nav className="hidden md:flex space-x-1">
              {desktopMenus.map((menu) => (
                <div
                  key={menu.id}
                  className="relative dropdown-container"
                  onMouseEnter={() => handleMouseEnter(menu.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <Link
                    to={`/${menu.id}${menu.submenu.length > 0 ? `?tab=${menu.submenu[0].id.split('?tab=')[1]}` : ''}`}
                    className={`px-4 py-2 text-sm font-medium transition-colors hover:text-secondary flex items-center ${
                      activeMenu === menu.id ? 'text-secondary' : 'text-white'
                    }`}
                  >
                    {menu.name}
                    {menu.submenu.length > 0 && (
                      <svg 
                        className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                          dropdownOpen === menu.id ? 'rotate-180' : ''
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </Link>
                  
                  {/* 드롭다운 메뉴 */}
                  {menu.submenu.length > 0 && (
                    <div
                      className={`absolute top-full left-0 mt-1 w-48 bg-black/80 backdrop-blur-sm rounded-lg shadow-2xl border border-white/20 overflow-hidden transition-all duration-300 transform ${
                        dropdownOpen === menu.id 
                          ? 'opacity-100 visible translate-y-0' 
                          : 'opacity-0 invisible -translate-y-2'
                      }`}
                      style={{ zIndex: 1000 }}
                    >
                      {menu.submenu.map((submenu, index) => (
                        <Link
                          key={index}
                          to={`/${submenu.id}`}
                          className="block px-4 py-3 text-sm text-white hover:bg-white/10 hover:text-secondary transition-all duration-200 border-b border-white/10 last:border-b-0"
                        >
                          {submenu.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
            
            {/* 모바일 메뉴 버튼 */}
            <div className="md:hidden ml-auto">
              <button 
                className="text-white hover:text-secondary"
                onClick={toggleMobileMenu}
                aria-label="메뉴 열기"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6"
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d={mobileMenuOpen 
                      ? "M6 18L18 6M6 6l12 12" // X 아이콘
                      : "M4 6h16M4 12h16M4 18h16" // 햄버거 아이콘
                    }
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* 모바일 메뉴 (헤더와 분리) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[200] md:hidden">
          {/* 반투명 오버레이 */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={closeMobileMenu}
          ></div>
          
          {/* 메뉴 사이드바 */}
          <div className="absolute top-0 right-0 bottom-0 w-4/5 max-w-sm bg-white z-[201] shadow-lg transform transition-transform duration-300 ease-in-out">
            {/* 메뉴 헤더 */}
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="font-bold text-lg text-primary">메뉴</h2>
              <button 
                onClick={closeMobileMenu}
                className="text-gray-500 hover:text-primary p-2"
                aria-label="메뉴 닫기"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M6 18L18 6M6 6l12 12" 
                  />
                </svg>
              </button>
            </div>
            
            {/* 메뉴 내용 */}
            <nav className="py-4 bg-white">
              {menus.map((menu) => (
                <div key={menu.id}>
                  {menu.submenu.length > 0 ? (
                    <div>
                      <button
                        onClick={() => toggleMobileDropdown(menu.id)}
                        className={`w-full flex items-center justify-between px-6 py-4 text-base font-medium transition-colors hover:bg-gray-100 ${
                          activeMenu === menu.id ? 'text-secondary border-l-4 border-secondary pl-5' : 'text-primary'
                        }`}
                      >
                        <span>{menu.name}</span>
                        <svg 
                          className={`w-5 h-5 transition-transform duration-200 ${
                            mobileDropdownOpen === menu.id ? 'rotate-180' : ''
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {/* 모바일 서브메뉴 */}
                      <div 
                        className={`overflow-hidden transition-all duration-300 ${
                          mobileDropdownOpen === menu.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        {menu.submenu.map((submenu, index) => (
                          <Link
                            key={index}
                            to={`/${submenu.id}`}
                            onClick={closeMobileMenu}
                            className="block px-12 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-primary transition-colors border-l-2 border-gray-200 ml-6"
                          >
                            {submenu.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={`/${menu.id}${menu.submenu.length > 0 ? `?tab=${menu.submenu[0].id.split('?tab=')[1]}` : ''}`}
                      className={`block px-6 py-4 text-base font-medium transition-colors hover:bg-gray-100 ${
                        activeMenu === menu.id ? 'text-secondary border-l-4 border-secondary pl-5' : 'text-primary'
                      }`}
                      onClick={closeMobileMenu}
                    >
                      {menu.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
            
            {/* 메뉴 푸터 */}
            <div className="absolute bottom-0 left-0 right-0 p-6 border-t text-center bg-white">
              <p className="text-sm text-gray-600">© {CURRENT_YEAR} 한앤손 부부치과</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;